import { ReactNode } from 'react';
import {
	Building2,
	CalendarDays,
	Gem,
	KeyRound,
	Loader2,
	Stethoscope,
	Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApproach, getHighlightTag } from '@/constants/professionalTags';
import {
	CANCEL_ACTOR_LABELS,
	PAYMENT_STATUS_LABELS,
	SESSION_STATUS_CLASSES,
	SESSION_STATUS_LABELS,
	SUBSCRIPTION_STATUS_CLASSES,
	SUBSCRIPTION_STATUS_LABELS,
	formatDateTime,
	formatMoney,
	fullName,
} from '../sandboxLabels';
import type { ImpersonationTarget } from './SandboxImpersonateModal';

/* ---------- blocos visuais ---------- */

const Section = ({
	icon,
	title,
	count,
	action,
	children,
	empty,
}: {
	icon: ReactNode;
	title: string;
	count: number;
	action?: ReactNode;
	children: ReactNode;
	empty: string;
}) => (
	<section className="space-y-3">
		<div className="flex flex-wrap items-center justify-between gap-2">
			<div className="flex items-center gap-2">
				<span className="text-stone-400">{icon}</span>
				<h2 className="text-sm font-black uppercase tracking-wide text-stone-500">
					{title}
				</h2>
				<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-500">
					{count}
				</span>
			</div>
			{action}
		</div>
		{count === 0 ? (
			<div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 px-4 py-5 text-center text-xs text-stone-400">
				{empty}
			</div>
		) : (
			<div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
				{children}
			</div>
		)}
	</section>
);

const Card = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => (
	<div
		className={cn(
			'flex flex-col gap-3 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm',
			className,
		)}
	>
		{children}
	</div>
);

const chipClass =
	'inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-600';

export const ActionButton = ({
	children,
	onClick,
	tone = 'default',
	disabled,
	title,
	icon,
	loading,
}: {
	children: ReactNode;
	onClick: () => void;
	tone?: 'default' | 'primary' | 'danger';
	disabled?: boolean;
	title?: string;
	/** Ícone à esquerda (slot fixo). */
	icon?: ReactNode;
	/** Mostra spinner no slot do ícone (não insere nós ao lado do texto). */
	loading?: boolean;
}) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled || loading}
		title={title}
		className={cn(
			'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
			tone === 'default' &&
				'border-stone-200 bg-white text-stone-700 hover:border-stone-900 hover:text-stone-900',
			tone === 'primary' &&
				'border-stone-900 bg-stone-900 text-white hover:bg-stone-800',
			tone === 'danger' &&
				'border-rose-200 bg-white text-rose-600 hover:border-rose-600',
		)}
	>
		{icon !== undefined || loading !== undefined ? (
			<span className="flex h-3 w-3 items-center justify-center">
				{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : icon}
			</span>
		) : null}
		<span className="inline-flex items-center gap-1.5">{children}</span>
	</button>
);

const Credentials = ({
	email,
	password,
}: {
	email: string;
	password: string | null;
}) => (
	<div className="rounded-xl bg-stone-50 px-3 py-2 text-[11px] text-stone-500">
		<span className="block truncate">
			<span className="font-bold text-stone-400">e-mail </span>
			<span className="select-all text-stone-700">{email}</span>
		</span>
		<span className="block">
			<span className="font-bold text-stone-400">senha </span>
			<span className="select-all text-stone-700">
				{password ?? '(não registrada)'}
			</span>
		</span>
	</div>
);

/* ---------- usuários ---------- */

interface UsersSectionProps {
	users: Sandbox.ScenarioUser[];
	onCreate: () => void;
	onSubscribe: (userId: string) => void;
	onSchedule: (userId: string) => void;
	onMatch: (userId: string) => void;
	onImpersonate: (target: ImpersonationTarget) => void;
}

export const SandboxUsersSection = ({
	users,
	onCreate,
	onSubscribe,
	onSchedule,
	onMatch,
	onImpersonate,
}: UsersSectionProps) => (
	<Section
		icon={<Users className="h-4 w-4" />}
		title="Usuários"
		count={users.length}
		empty="Nenhum usuário. Crie um para começar a jornada."
		action={
			<ActionButton onClick={onCreate} tone="primary">
				+ Usuário
			</ActionButton>
		}
	>
		{users.map((user) => (
			<Card key={user.id}>
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="truncate text-sm font-black text-stone-900">
							{fullName(user)}
						</p>
						<p className="text-[11px] text-stone-400">
							criado em {formatDateTime(user.created_at)}
						</p>
					</div>
					<div className="flex flex-wrap justify-end gap-1">
						{user.active_subscription ? (
							<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-700">
								{user.active_subscription.club.name}
							</span>
						) : (
							<span className={chipClass}>sem plano</span>
						)}
						{user.match_answered ? (
							<span className="rounded-full bg-brand-pink/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-pink">
								match
							</span>
						) : null}
						{user.employee ? (
							<span className={chipClass}>
								{user.employee.company.trade_name}
							</span>
						) : null}
					</div>
				</div>
				<Credentials email={user.email} password={user.password} />
				<div className="flex flex-wrap gap-1.5 text-[11px] text-stone-400">
					<span className={chipClass}>CPF {user.cpf ?? '—'}</span>
					<span className={chipClass}>
						{user.sessions_count} sessão(ões)
					</span>
					<span className={chipClass}>
						{user.verified_email
							? 'e-mail verificado'
							: 'e-mail pendente'}
					</span>
				</div>
				<div className="flex flex-wrap gap-1.5">
					<ActionButton
						onClick={() => onSubscribe(user.id)}
						disabled={Boolean(user.active_subscription)}
						title={
							user.active_subscription
								? 'Já tem assinatura ativa'
								: undefined
						}
					>
						<Gem className="h-3 w-3" /> Assinar plano
					</ActionButton>
					<ActionButton onClick={() => onSchedule(user.id)}>
						<CalendarDays className="h-3 w-3" /> Agendar
					</ActionButton>
					<ActionButton
						onClick={() => onMatch(user.id)}
						disabled={user.match_answered}
					>
						Match
					</ActionButton>
					<ActionButton
						onClick={() =>
							onImpersonate({
								kind: 'USER',
								id: user.id,
								label: fullName(user),
							})
						}
					>
						<KeyRound className="h-3 w-3" /> Entrar como
					</ActionButton>
				</div>
			</Card>
		))}
	</Section>
);

/* ---------- profissionais ---------- */

interface ProfessionalsSectionProps {
	professionals: Sandbox.ScenarioProfessional[];
	onCreate: () => void;
	onSchedule: (professionalId: string) => void;
	onImpersonate: (target: ImpersonationTarget) => void;
}

export const SandboxProfessionalsSection = ({
	professionals,
	onCreate,
	onSchedule,
	onImpersonate,
}: ProfessionalsSectionProps) => (
	<Section
		icon={<Stethoscope className="h-4 w-4" />}
		title="Profissionais"
		count={professionals.length}
		empty="Nenhum profissional. Crie um com agenda para poder agendar."
		action={
			<ActionButton onClick={onCreate} tone="primary">
				+ Profissional
			</ActionButton>
		}
	>
		{professionals.map((pro) => (
			<Card key={pro.id}>
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="truncate text-sm font-black text-stone-900">
							{fullName(pro)}
						</p>
						<p className="truncate text-[11px] text-stone-400">
							{pro.crm_or_crp} ·{' '}
							{pro.services.map((s) => s.service_name).join(', ')}
						</p>
					</div>
					<div className="flex flex-wrap justify-end gap-1">
						<span
							className={cn(
								'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide',
								pro.authorized
									? 'bg-emerald-50 text-emerald-700'
									: 'bg-amber-50 text-amber-700',
							)}
						>
							{pro.authorized ? 'autorizado' : 'aguardando'}
						</span>
						<span className={chipClass}>
							{pro.visibility === 'PUBLIC_SEARCH'
								? 'visível'
								: 'oculto'}
						</span>
					</div>
				</div>
				<Credentials email={pro.email} password={pro.password} />
				<div className="flex flex-wrap gap-1.5">
					<span className={chipClass}>
						{pro.availability_count} janela(s) de agenda
					</span>
					<span className={chipClass}>
						{pro.sessions_count} sessão(ões)
					</span>
					{pro.main_approach ? (
						<span className={chipClass}>
							{getApproach(pro.main_approach)?.short ??
								pro.main_approach}
						</span>
					) : null}
					{pro.highlight_tag ? (
						<span className={chipClass}>
							{getHighlightTag(pro.highlight_tag)?.label ??
								pro.highlight_tag}
						</span>
					) : null}
				</div>
				<div className="flex flex-wrap gap-1.5">
					<ActionButton
						onClick={() => onSchedule(pro.id)}
						disabled={!pro.authorized}
					>
						<CalendarDays className="h-3 w-3" /> Agendar com ele(a)
					</ActionButton>
					<ActionButton
						onClick={() =>
							onImpersonate({
								kind: 'PROFESSIONAL',
								id: pro.id,
								label: fullName(pro),
							})
						}
					>
						<KeyRound className="h-3 w-3" /> Entrar como
					</ActionButton>
				</div>
			</Card>
		))}
	</Section>
);

/* ---------- assinaturas ---------- */

interface SubscriptionsSectionProps {
	subscriptions: Sandbox.ScenarioSubscription[];
	onCreate: () => void;
	onCancel: (subscriptionId: string) => void;
	pendingId?: string | null;
}

export const SandboxSubscriptionsSection = ({
	subscriptions,
	onCreate,
	onCancel,
	pendingId,
}: SubscriptionsSectionProps) => (
	<Section
		icon={<Gem className="h-4 w-4" />}
		title="Assinaturas"
		count={subscriptions.length}
		empty="Nenhuma assinatura. Ative um plano para um usuário."
		action={
			<ActionButton onClick={onCreate} tone="primary">
				+ Assinatura
			</ActionButton>
		}
	>
		{subscriptions.map((sub) => (
			<Card key={sub.id}>
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="truncate text-sm font-black text-stone-900">
							{sub.club.name}{' '}
							<span className="font-medium text-stone-400">
								· {formatMoney(sub.club.price)}/mês
							</span>
						</p>
						<p className="truncate text-[11px] text-stone-400">
							{fullName(sub.user)} · desde{' '}
							{formatDateTime(sub.start_date)}
						</p>
					</div>
					<span
						className={cn(
							'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide',
							SUBSCRIPTION_STATUS_CLASSES[sub.status] ??
								'bg-stone-100 text-stone-500',
						)}
					>
						{SUBSCRIPTION_STATUS_LABELS[sub.status] ?? sub.status}
					</span>
				</div>
				<ul className="space-y-1">
					{sub.benefits.map((b) => (
						<li
							key={b.id}
							className="flex items-center justify-between text-xs text-stone-600"
						>
							<span>
								{b.service_name} (
								{b.service_discount_percentage}%)
							</span>
							<span className="font-bold text-stone-900">
								{b.used_count}/{b.default_amount} usados
							</span>
						</li>
					))}
				</ul>
				{sub.status === 'ACTIVE' ? (
					<div>
						<ActionButton
							tone="danger"
							onClick={() => onCancel(sub.id)}
							loading={pendingId === sub.id}
						>
							Cancelar assinatura
						</ActionButton>
					</div>
				) : sub.cancelled_reason ? (
					<p className="text-[11px] text-stone-400">
						{sub.cancelled_reason}
					</p>
				) : null}
			</Card>
		))}
	</Section>
);

/* ---------- sessões ---------- */

interface SessionsSectionProps {
	sessions: Sandbox.ScenarioSession[];
	onCreate: () => void;
	onPay: (sessionId: string, mode: 'MANUAL' | 'BENEFIT') => void;
	onStart: (sessionId: string) => void;
	onEnd: (sessionId: string) => void;
	onCancel: (sessionId: string, by: Sandbox.CancelActor) => void;
	pendingId?: string | null;
}

export const SandboxSessionsSection = ({
	sessions,
	onCreate,
	onPay,
	onStart,
	onEnd,
	onCancel,
	pendingId,
}: SessionsSectionProps) => (
	<Section
		icon={<CalendarDays className="h-4 w-4" />}
		title="Sessões"
		count={sessions.length}
		empty="Nenhuma sessão. Agende uma como o usuário faria."
		action={
			<ActionButton onClick={onCreate} tone="primary">
				+ Sessão
			</ActionButton>
		}
	>
		{sessions.map((session) => {
			const isPending = pendingId === session.id;
			const lastPayment = session.payments[session.payments.length - 1];
			return (
				<Card key={session.id} className="xl:col-span-2">
					<div className="flex flex-wrap items-start justify-between gap-2">
						<div className="min-w-0">
							<p className="text-sm font-black text-stone-900">
								{session.service.service_name} ·{' '}
								{formatDateTime(session.selected_date)}
							</p>
							<p className="truncate text-[11px] text-stone-400">
								{session.user ? fullName(session.user) : '—'}{' '}
								com {fullName(session.professional)} ·{' '}
								{session.is_online ? 'online' : 'presencial'}
							</p>
						</div>
						<span
							className={cn(
								'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide',
								SESSION_STATUS_CLASSES[session.status],
							)}
						>
							{SESSION_STATUS_LABELS[session.status]}
						</span>
					</div>

					<div className="flex flex-wrap gap-1.5">
						{session.payments.length === 0 ? (
							<span className={chipClass}>sem pagamento</span>
						) : (
							session.payments.map((p) => (
								<span key={p.id} className={chipClass}>
									{p.gateway} ·{' '}
									{PAYMENT_STATUS_LABELS[p.status] ??
										p.status}{' '}
									· {formatMoney(p.amount)}
									{p.benefit_applied
										? ` (benefício −${formatMoney(
												p.benefit_applied
													.amount_discounted,
										  )})`
										: ''}
								</span>
							))
						)}
						{session.started_at ? (
							<span className={chipClass}>
								início {formatDateTime(session.started_at)}
							</span>
						) : null}
						{session.finished_at ? (
							<span className={chipClass}>
								fim {formatDateTime(session.finished_at)}
							</span>
						) : null}
						{session.scheduling_error ? (
							<span
								className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700"
								title={session.scheduling_error}
							>
								aviso de agenda
							</span>
						) : null}
					</div>

					<div className="flex flex-wrap items-center gap-1.5">
						{isPending ? (
							<Loader2 className="h-4 w-4 animate-spin text-stone-400" />
						) : null}
						{session.status === 'CREATED' &&
						lastPayment?.status !== 'DONE' ? (
							<>
								<ActionButton
									tone="primary"
									onClick={() => onPay(session.id, 'MANUAL')}
									disabled={isPending}
								>
									Pagar (manual)
								</ActionButton>
								<ActionButton
									onClick={() => onPay(session.id, 'BENEFIT')}
									disabled={isPending}
								>
									Pagar com benefício
								</ActionButton>
							</>
						) : null}
						{session.status === 'CONFIRMED' ? (
							<ActionButton
								tone="primary"
								onClick={() => onStart(session.id)}
								disabled={isPending}
								title="Adianta o relógio para o horário agendado e inicia como o profissional"
							>
								Iniciar (profissional)
							</ActionButton>
						) : null}
						{session.status === 'STARTED' ? (
							<ActionButton
								tone="primary"
								onClick={() => onEnd(session.id)}
								disabled={isPending}
								title="Considera 50 min decorridos e finaliza como o profissional"
							>
								Finalizar (profissional)
							</ActionButton>
						) : null}
						{session.status !== 'CANCELLED' &&
						session.status !== 'FINISHED' ? (
							<span className="ml-auto inline-flex flex-wrap items-center gap-1">
								<span className="text-[10px] font-black uppercase tracking-wide text-stone-300">
									cancelar
								</span>
								{(
									Object.keys(
										CANCEL_ACTOR_LABELS,
									) as Sandbox.CancelActor[]
								).map((by) => (
									<ActionButton
										key={by}
										tone="danger"
										onClick={() => onCancel(session.id, by)}
										disabled={isPending}
									>
										{CANCEL_ACTOR_LABELS[by]}
									</ActionButton>
								))}
							</span>
						) : null}
					</div>
				</Card>
			);
		})}
	</Section>
);

/* ---------- empresas ---------- */

interface CompaniesSectionProps {
	companies: Sandbox.ScenarioCompany[];
	onCreate: () => void;
}

export const SandboxCompaniesSection = ({
	companies,
	onCreate,
}: CompaniesSectionProps) => (
	<Section
		icon={<Building2 className="h-4 w-4" />}
		title="Empresas"
		count={companies.length}
		empty="Nenhuma empresa. Crie uma para simular o fluxo B2B."
		action={
			<ActionButton onClick={onCreate} tone="primary">
				+ Empresa
			</ActionButton>
		}
	>
		{companies.map((company) => (
			<Card key={company.id}>
				<div>
					<p className="text-sm font-black text-stone-900">
						{company.trade_name}
					</p>
					<p className="text-[11px] text-stone-400">
						CNPJ {company.cnpj ?? '—'} ·{' '}
						{company.clubs.length > 0
							? company.clubs.map((c) => c.name).join(', ')
							: 'sem clube vinculado'}
					</p>
				</div>
				{company.employees.length > 0 ? (
					<ul className="space-y-1">
						{company.employees.map((e) => (
							<li
								key={e.id}
								className="flex items-center justify-between text-xs text-stone-600"
							>
								<span>{e.user_name ?? `CPF ${e.cpf}`}</span>
								<span className={chipClass}>
									{e.linked_subscription_id
										? 'assinatura B2B2C'
										: e.user_id
										  ? 'vinculado'
										  : 'sem conta'}
								</span>
							</li>
						))}
					</ul>
				) : (
					<p className="text-xs text-stone-400">Sem colaboradores.</p>
				)}
			</Card>
		))}
	</Section>
);
